import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Container, Section, Eyebrow } from '@/components/primitives/Container';
import { GLOSSARY } from '@/data/glossary';

export function Glosario() {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GLOSSARY;
    return GLOSSARY.filter(
      (t) =>
        t.term.toLowerCase().includes(q) ||
        t.definition.toLowerCase().includes(q) ||
        t.short?.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <>
      <Section className="pt-12 sm:pt-16">
        <Container width="editorial">
          <Eyebrow>Glosario</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-normal tracking-tight sm:text-5xl">
            El vocabulario que SICA usa con precisión.
          </h1>
          <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink-muted">
            La marca usa estas palabras intencionalmente. Cada término tiene un significado curricular
            específico que SICA preserva.
          </p>
        </Container>
      </Section>

      <Section className="pt-0">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[16rem_1fr]">
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                    Filtrar
                  </span>
                  <div className="mt-2 flex items-center rounded-md border border-line bg-paper">
                    <Search size={14} className="ml-3 text-ink-subtle" aria-hidden="true" />
                    <input
                      type="search"
                      placeholder="Buscar término…"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="focus-ring w-full bg-transparent px-3 py-2 text-sm text-ink placeholder:text-ink-subtle"
                    />
                  </div>
                </label>
                <p className="mt-3 text-xs text-ink-subtle">
                  {filtered.length} de {GLOSSARY.length} términos
                </p>
                <nav className="mt-6" aria-label="Índice del glosario">
                  <ul className="flex flex-col gap-1">
                    {filtered.map((t) => (
                      <li key={t.id}>
                        <a
                          href={`#${t.id}`}
                          className="focus-ring block rounded text-sm text-ink-muted hover:text-ink"
                        >
                          {t.term}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            <div className="max-w-editorial">
              <label className="mb-8 block lg:hidden">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                  Filtrar
                </span>
                <input
                  type="search"
                  placeholder="Buscar término…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="focus-ring mt-2 w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
                />
              </label>

              <ul className="flex flex-col">
                {filtered.map((t) => (
                  <li
                    key={t.id}
                    id={t.id}
                    className="glossary-term border-t border-line py-8 first:border-t-0 scroll-mt-24"
                  >
                    <h2 className="font-serif text-xl font-normal tracking-tight text-ink">
                      {t.term}
                      {t.short && (
                        <span className="ml-3 text-xs font-normal italic text-ink-muted">
                          {t.short}
                        </span>
                      )}
                    </h2>
                    <p className="mt-3 text-base leading-relaxed text-ink">{t.definition}</p>
                    {t.example && (
                      <p className="mt-3 text-sm italic text-ink-muted">
                        Ejemplo: {t.example}
                      </p>
                    )}
                    {t.related && t.related.length > 0 && (
                      <p className="mt-3 text-xs text-ink-subtle">
                        Relacionado: {t.related.join(' · ')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>

              {filtered.length === 0 && (
                <p className="rounded-md border border-dashed border-line p-6 text-sm text-ink-muted">
                  No hay términos que coincidan con la búsqueda.
                </p>
              )}
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
